<template>
  <div
    ref="sectionClass"
    v-bind:class="sectionClass"
    v-bind:style="cellStyle"
    v-on:contextmenu.prevent="dispatchOpenContextMenu"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd"
    v-if="!isGroup"
  >
    <button
      class="timetable-cell"
      type="button"
      v-bind:class="cell.tags"
      v-bind:key="index"
      v-bind:title="title"
      v-for="(isOver, index) in slots"
      v-on:click="onClickSlot(cell)"
      v-on:dragenter.prevent="onDragAppointmentEnter(index)"
      v-on:dragleave.prevent="onDragAppointmentLeave(index)"
      v-on:dragover.prevent
      v-on:drop.prevent="onDropAppointment(index)"
      :disabled="isEmpty"
    >
      <span
        v-if="isOver"
        class="timetable-cell__over"
        v-bind:style="getOverCSS(cell)" />
    </button>
    <span
      v-if="isAdd"
      class="timetable-cell__interval"
    >{{ cell.interval }}</span>
    <timetable-patient-cell
      class="timetable-cell__patient"
      v-bind:appointment-id="cell.id"
      v-bind:can-create-invoice="canCreateInvoice"
      v-bind:can-initiate-calls="canInitiateCalls"
      v-bind:color="cell.color"
      v-bind:expanded-slot="getExpandedPatientSlot(cell)"
      v-bind:interval="cell.interval"
      v-bind:is-initial="cell.patient.is_initial"
      v-bind:note="croppedNote"
      v-bind:patient-name="cell.patient.name"
      v-bind:patient-full-name="cell.patient.full_name"
      v-bind:patient-card-number="cell.patient.card_number"
      v-bind:ratio-step="cell.ratio_to_step"
      v-bind:show-employee-name="showEmployeeName"
      v-bind:style="{ backgroundColor: cell.color }"
      v-bind:tags="cell.tags"
      v-bind:has-debt="cell.has_debt"
      v-bind:has-advance="cell.has_advance"
      v-bind:has-view-patient-invoices="hasViewPatientInvoices"
      v-bind:patient-birth-date="cell.patient.birth_date"
      v-bind:date="date"
      v-if="isPatient"
      v-on:click="dispatchOpenAppointment(cell)"
      v-on:create-invoice="onCreateInvoice(cell.id)"
      v-on:open-invoices="onOpenInvoices(cell.patient.id)"
      v-on:dragend="onDragAppointmentEnd"
      v-on:dragstart="onDragAppointmentStart(cell)"
      v-on:initiate-call="onInitiateCall" />
    <timetable-break-cell
      v-if="isBreak"
      v-bind:interval="cell.interval"
      v-bind:note="cell.note"
      v-bind:tags="cell.tags"
      v-bind:title="title"
      v-on:open="openBreak"
    />
    <button
      class="timetable-cell__waiting"
      type="button"
      v-bind:title="`Запись из листа ожидания ${title}`"
      v-if="hasWaitings"
      v-on:click="openWaitings">{{ numWaitings }}</button>
  </div>
  <div
    class="timetable-cell__group"
    v-bind:style="getCellCSS(cell)"
    v-else
  >
    <timetable-patient-cell
      v-bind:appointment-id="patientSlot.id"
      v-bind:can-create-invoice="canCreateInvoice"
      v-bind:can-initiate-calls="canInitiateCalls"
      v-bind:interval="patientSlot.interval"
      v-bind:is-initial="patientSlot.patient.is_initial"
      v-bind:patient-card-number="cell.patient.card_number"
      v-bind:key="patientSlot.id"
      v-bind:patient-name="patientSlot.patient.name"
      v-bind:ratio-step="cell.ratio_to_step"
      v-bind:tags="patientSlot.tags"
      v-bind:date="date"
      v-for="patientSlot in getAppointmentSlots(cell)"
      v-on:click="dispatchOpenAppointment(patientSlot)"
      v-on:create-invoice="onCreateInvoice(patientSlot.id)"
      v-on:dragend="onDragAppointmentEnd"
      v-on:dragstart="onDragAppointmentStart(patientSlot)"
      v-on:initiate-call="onInitiateCall" />
    <popover-menu
      v-if="cell.patients.length > cell.ratio_to_step"
      v-bind:attach="false"
      v-bind:offset-y="true"
      v-bind:offset-x="true">
      <template v-slot:trigger="{ listenerObject }">
        <div
          v-on="listenerObject"
          alt="Еще записей на прием"
          class="timetable-cell__group__counts"
          :title="`Еще записей на прием: ${cell.patients.length - cell.ratio_to_step}`" />
      </template>
      <form-select-timetable-cell
        v-bind:list="getHiddenAppointmentSlots(cell)"
        v-on:select="dispatchOpenAppointment" />
    </popover-menu>
  </div>
</template>

<script>
import FormSelectTimetableCell from '@/components/FormSelectTimetableCell.vue';
import PopoverMenu from '@/components/PopoverMenu.vue';
import TimetablePatientCell from '@/components/TimetablePatientCell.vue';
import TimetableBreakCell from './TimetableBreakCell.vue';

/**
 * @description Действие инициировать звонки.
 * @type {string}
 */
const ACTION_INITIATE_CALLS = 'initiate-calls';

/**
 * @description Название события создания счета.
 * @type {string}
 */
const NAME_EVENT_CREATE_INVOICE = 'create-invoice';

/**
 * @description Название события просмотра счетов.
 * @type {string}
 */
const NAME_EVENT_OPEN_INVOICES = 'open-invoices';

/**
 * @description Название события инициации звонка.
 * @type {string}
 */
const NAME_EVENT_INITIATE_CALL = 'initiate-call';

/**
 * @description Название события нажатия на деактивированный слот.
 * @type {string}
 */
const NAME_EVENT_CLICK_DISABLED_CELL = 'click-disabled-cell';

/**
 * @description Необходимые для подсветки статусы записей.
 * @type {array<string>}
 */
const ENTRY_HIGHLIGHT_STATUSES = ['noconfirm', 'confirm', 'came'];

/**
 * @description Высота единичного шага.
 * @type {number}
 */
const STEP_HEIGHT = 53;

export default {
  components: {
    PopoverMenu,
    FormSelectTimetableCell,
    TimetablePatientCell,
    TimetableBreakCell,
  },
  computed: {
    cellStyle() {
      const selfHeight = this.cell.ratio_to_step * STEP_HEIGHT;
      const height = `${Math.max(1, selfHeight)}px`;
      const style = {
        height,
      };
      if (this.cell.color) {
        style.backgroundColor = this.cell.color;
      }
      return style;
    },
    /**
     * @description Можно создать счет для записи на прием.
     * @type {boolean}
     */
    canCreateInvoice() {
      const cell = this.isGroup ? this.cell.patients[0] : this.cell;
      return cell.actions.includes('create-invoice');
    },
    /**
     * @description Может инициировать звонки.
     * @returns {boolean}
     */
    canInitiateCalls() {
      const cell = this.isGroup ? this.cell.patients[0] : this.cell;
      return cell.actions.includes(ACTION_INITIATE_CALLS);
    },
    /**
     * @description Имеет доступные записи из листа ожидания.
     * @type {boolean}
     */
    hasWaitings() {
      return false; // this.numWaitings > 0; Смотреть в добавление waiting
    },
    /**
     * @description Перерыв.
     */
    isBreak: ({ cell }) => cell.tags.indexOf('break') !== -1,
    isAdd() {
      return this.cell.tags.includes('add');
    },
    /**
     * @description Пустой слот.
     */
    isEmpty: ({ cell }) => cell.tags.includes('empty'),
    /**
     * @description Деактивирован.
     */
    isDisabled: ({ cell, isAdd }) => !isAdd && cell.tags.indexOf('busy') === -1,
    /**
     * @description Группа слотов.
     */
    isGroup: ({ cell }) => cell.tags.indexOf('group') !== -1,
    /**
     * @description Наличие данных пациента.
     */
    isPatient: ({ cell }) => !!cell.patient,
    /**
     * @description Текст при наведении.
     */
    title: ({ cell }) => `с ${cell.starts_time} по ${cell.ends_time}`,
    /**
     * @description Количество записей листа ожидания.
     * @returns {number}
     */
    numWaitings() {
      return this.waitingIds.length;
    },
    /**
     * @description Отношение к шагу.
     * @returns {number}
     */
    ratioToStep: ({ cell }) => cell.ratio_to_step,
    sectionClass() {
      if (this.hasWaitings) {
        return 'timetable-cell__split-section';
      }
      return 'timetable-cell__section';
    },
    /**
     * @description Время начала слотов.
     * @returns {string[]}
     */
    timeStarts: ({ cell, ratioToStep }) => {
      const makeDateTime = (time) => {
        const [hour, minute] = time.split(':');
        const dateTime = new Date(0);
        dateTime.setHours(hour, minute, 0, 0);
        return dateTime;
      };
      const makeTime = (time) => {
        const hour = time.getHours().toString().padStart(2, '0');
        const min = time.getMinutes().toString().padStart(2, '0');
        return `${hour}:${min}`;
      };
      const starts = makeDateTime(cell.starts_time);
      const ends = makeDateTime(cell.ends_time);
      const interval = ends - starts;
      const timeStep = interval / ratioToStep;
      const result = [];
      let i = 0;
      do {
        result[i] = makeTime(new Date(starts.getTime() + i * timeStep));
        i += 1;
      } while (i < ratioToStep);
      return result;
    },
    /**
     * @description Идентификаторы записей из листа ожидания.
     * @returns {number[]}
     */
    waitingIds() {
      return (this.cell.waitings || []).map(({ id }) => id);
    },
    /**
     * @description Обрезать заметку до 100 символов
     * @returns {string}
     */
    croppedNote() {
      return this.cell.note.length > 100
        ? `${this.cell.note.substring(0, 100)}...`
        : this.cell.note;
    },
  },
  data() {
    return {
      /**
       * @description Слоты для перемещения.
       * @param {boolean[]}
       */
      slots: Array(Math.ceil(this.cell.ratio_to_step)).fill(false),
      touchTimeout: null,
      isTouchDevice: false,
    };
  },
  mounted() {
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  },
  methods: {
    /**
     * @description Обработать нажатие на слот.
     * @param {any} slot
     * @returns {void}
     */
    onClickSlot(slot) {
      if (this.isDisabled) {
        this.$emit(NAME_EVENT_CLICK_DISABLED_CELL);
      } else {
        this.dispatchOpenAppointment(slot);
      }
    },
    /**
     * @description Обработать создание счета.
     * @param {number} appointmentId Идентификатор записи на прием.
     * @returns {void}
     */
    onCreateInvoice(appointmentId) {
      this.$emit(NAME_EVENT_CREATE_INVOICE, appointmentId);
    },
    /**
     * @description Обработать переход к счетам.
     * @param {number} patientId Идентификатор пациента.
     * @returns {void}
     */
    onOpenInvoices(patientId) {
      this.$emit(NAME_EVENT_OPEN_INVOICES, patientId);
    },
    /**
     * @description Обработать начало перетаскивания записи на прием.
     * @returns {void}
     */
    onDragAppointmentStart(slot) {
      const appointment = {
        employeeId: slot.employee ? slot.employee.id : 0,
        id: slot.id,
        ratioToStep: slot.ratio_to_step,
      };
      this.$emit('drag-appointment-start', appointment);
    },
    /**
     * @description Обработать окончание перетаскивания записи на прием.
     * @returns {void}
     */
    onDragAppointmentEnd() {
      const appointment = {
        id: this.cell.id,
        ratioToStep: this.ratioToStep,
      };
      this.$emit('drag-appointment-end', appointment);
    },
    /**
     * @description Обработать установку записи на прием.
     * @param {number} localPosition
     * @returns {void}
     */
    onDropAppointment(localPosition = 0) {
      if (this.slots[localPosition] === true) {
        const time = this.timeStarts[localPosition];
        this.$emit('move-appointment', time);
        this.slots = this.slots.fill(false);
      }
    },
    /**
     * @description Обработать наведение записи на прием на слот.
     * @param {number} localPosition
     * @returns {void}
     */
    onDragAppointmentEnter(localPosition = 0) {
      if (!this.isPatient || this.cell.id === this.dragAppointment.id) {
        const isFree = this.canMoveAppointment(this.cell, localPosition);
        this.$set(this.slots, localPosition, isFree);
      }
    },
    /**
     * @description Обработать уход записи на прием со слота.
     * @param {number} localPosition
     * @returns {void}
     */
    onDragAppointmentLeave(localPosition = 0) {
      this.$set(this.slots, localPosition, false);
    },
    /**
     * @description Обработать инициацию звонка.
     * @param {number} appointmentId
     * @returns {void}
     */
    onInitiateCall(appointmentId) {
      this.$emit(NAME_EVENT_INITIATE_CALL, appointmentId);
    },
    openBreak() {
      if (this.hasEditBreaks) {
        this.$emit('open-break');
      }
    },
    /**
     * Получить расширенный слот.
     * @param {any} cell
     * @returns {any}
     */
    getExpandedPatientSlot(cell) {
      return this.expandedSlots.find((item) => item.id === cell.id);
    },
    /**
     * @description Отправить событие открытия записей листа ожидания.
     * @returns {void}
     */
    openWaitings() {
      this.$emit('open-waitings', this.waitingIds);
    },
    /**
     * @description Отправить событие открытия записи на прием.
     * @param {any} cell
     * @returns {void}
     */
    dispatchOpenAppointment(cell) {
      this.$emit('open-appointment', cell);
    },
    /**
     * @description Отправить событие открытия контекстного меню.
     * @param {MouseEvent} event Событие.
     * @returns {void}
     */
    dispatchOpenContextMenu(event) {
      const offset = {
        x: event.pageX,
        y: event.pageY,
      };
      const recordingStatus = this.cell.tags
        .find((tag) => ENTRY_HIGHLIGHT_STATUSES.includes(tag));
      const data = {
        offset,
        currentStatus: recordingStatus ?? '',
        slot: this.cell,
        target: this.$refs.contextMenuTarget,
      };
      this.$emit('open-context-menu', data);
    },
    handleTouchStart(event) {
      this.touchTimeout = setTimeout(() => {
        this.dispatchOpenContextMenu(event);
      }, 500); // Время удержания в миллисекундах
    },
    handleTouchEnd() {
      clearTimeout(this.touchTimeout);
    },
    /**
     * @description Получить слоты для показа.
     * @param {any} cell
     * @returns {any[]}
     */
    getAppointmentSlots(cell) {
      return cell.patients.slice(0, cell.ratio_to_step);
    },
    /**
     * @description Получить стили для слота.
     * @param {any} cell Слот.
     */
    getCellCSS(cell) {
      const backgroundColor = cell.color;
      const selfHeight = cell.ratio_to_step * STEP_HEIGHT;
      const levelingHeight = (cell.ratio_to_step - 1) * 8;
      const fullHeight = selfHeight + levelingHeight;
      const height = `${fullHeight}px`;
      return {
        backgroundColor,
        height,
        minHeight: height,
      };
    },
    getOverCSS() {
      const { ratioToStep } = this.dragAppointment;
      const fullHeight = ratioToStep * STEP_HEIGHT;
      const height = `${fullHeight}px`;
      return {
        height,
        minHeight: height,
      };
    },
    /**
     * @description Получить скрытые слоты для показа.
     * @param {any} cell
     * @returns {any[]}
     */
    getHiddenAppointmentSlots(cell) {
      return cell.patients.slice(cell.ratio_to_step);
    },
  },
  watch: {
    'cell.ratio_to_step': {
      handler(step) {
        this.slots = Array(Math.ceil(step)).fill(false);
      },
    },
  },
  props: {
    /**
     * @description Функция проверки возможности перемещения записи на прием.
     * @type {function}
     */
    canMoveAppointment: { required: true, type: Function },
    /**
     * @description Слот.
     * @type {any}
     */
    cell: { required: true, type: Object },
    /**
     * @description Перемещаемый слот.
     * @type {any}
     */
    dragAppointment: { required: true, type: Object },
    /**
     * @description Список расширенных слотов.
     * @type {any[]}
     */
    expandedSlots: { required: true, type: Array },
    /**
     * @description Возможность редактирования перерыва.
     * @type {boolean}
     */
    hasEditBreaks: Boolean,
    /**
     * @description Показывать имя сотрудника.
     * @type {boolean}
     */
    showEmployeeName: { type: Boolean, default: false },
    /**
     * @description Может просматривать счета пациентов.
     * @type {boolean}
     */
    hasViewPatientInvoices: { type: Boolean, default: false },
    /**
     * @description Дата в формате ISO.
     * @type {string}
     */
    date: { type: String, default: '' },
  },
};
</script>

<style lang="scss">
.timetable-cell {
  position: relative;
  background-color: var(--fill-color);
  height: 100%;
  width: 100%;
  &__patient {
    width: 100%;
    height: 100%;
    position: absolute;
    cursor: pointer;
  }
  &__section,
  &__split-section {
    display: flex;
    gap: .5rem;
    position: relative;
  }
  &__section {
    border: 1px solid rgba(0, 0, 0, 0.12);
    flex-direction: column;
    justify-content: center;
    &:nth-of-type(n + 16):nth-last-child(-n + 10){
      .timetable-patient-cell {
        &__hover {
          top: auto;
          bottom: -1px;
        }
      }
    }
  }
  &__split-section {
    flex-direction: row;
  }
  &__split-section > button {
    background-color: var(--fill-color);
    border-radius: var(--block-border-radius);
    width: 100%;
  }
  &__waiting:before {
    content: '\231B';
  }
  &.section {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
  }
  &__interval {
    display: none;
    position: absolute;
    text-align: center;
    left: 0;
    right: 0;
    pointer-events: none;
  }
  &.add {
    &:before {
      content: "＋";
      font-weight: 300;
      font-size: 12px;
      cursor: pointer;
    }
    &:hover {
      &:before {
        content: none;
      }
    }
  }
  &.add:hover ~ &__interval {
    display: block;
    color: var(--action-color);
  }
  &.add:hover,
  &__waiting:hover {
    color: var(--action-color);
    filter: brightness(0.95);
    font-weight: 900;
  }
  &.busy {
    background-color: var(--unfocus-color);
  }
  &.empty {
    background-color: var(--primary-background-color);
    cursor: default;
  }
  &__over {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    z-index: 2;
    pointer-events: none;
    border: 2px dashed var(--action-color);
    border-radius: var(--block-border-radius);
  }
  &__group {
    border-radius: var(--block-border-radius);
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    gap: 4px;
    & > * {
      cursor: pointer;
      padding: 0 14px !important;
    }
    &__counts {
      position: absolute;
      background-image: url("@/assets/img/exit-black.svg");
      background-size: contain;
      background-position: center;
      transform: rotate(45deg);
      right: 8px;
      top: 8px;
      width: 10px;
      height: 10px;
    }
  }
}
</style>
